import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import ptmService from '../../services/ptmService';
import staffService from '../../services/staffService';
import DialogModal from '../../components/DialogModal';
import { CalendarCheck, X } from '@phosphor-icons/react';

function todayISO() {
  return new Date().toISOString().split('T')[0];
}

function ParentPTMSection({ kids }) {
  const [teachers, setTeachers] = useState([]);
  const [teacherFilter, setTeacherFilter] = useState('');
  const [slots, setSlots] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingSlotId, setBookingSlotId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(kids?.[0]?.id || '');
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const loadSlots = async (teacherId) => {
    try {
      const data = await ptmService.listOpenSlots(teacherId || null, todayISO());
      setSlots(data.slots || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load available slots');
    }
  };

  const loadBookings = async () => {
    try {
      const data = await ptmService.listMyParentBookings();
      setBookings((data.bookings || []).filter((b) => b.status === 'confirmed'));
    } catch (err) {
      toast.error(err.message || 'Failed to load your meetings');
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [teachersData] = await Promise.all([staffService.listTeachers(), loadSlots(teacherFilter), loadBookings()]);
      setTeachers(Array.isArray(teachersData) ? teachersData : (teachersData?.items || []));
    } catch (err) {
      toast.error(err.message || 'Failed to load meeting data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadSlots(teacherFilter); }, [teacherFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const startBooking = (slotId) => {
    setBookingSlotId(slotId);
    setSelectedStudentId(kids?.[0]?.id || '');
    setPurpose('');
  };

  const confirmBooking = async () => {
    if (!selectedStudentId) {
      toast.error('Choose which child this meeting is about');
      return;
    }
    setSubmitting(true);
    try {
      await ptmService.bookSlot(bookingSlotId, selectedStudentId, purpose);
      toast.success('Meeting booked — check your email for confirmation');
      setBookingSlotId(null);
      loadSlots(teacherFilter);
      loadBookings();
    } catch (err) {
      toast.error(err.message || 'Failed to book this slot');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelBooking = (bookingId) => {
    setConfirmDialog({
      title: 'Cancel Meeting',
      message: 'Cancel this meeting?',
      type: 'warning',
      confirmLabel: 'Cancel Meeting',
      onConfirm: async () => {
        setBusyId(bookingId);
        try {
          await ptmService.cancelBookingAsParent(bookingId);
          toast.success('Meeting cancelled');
          loadBookings();
          loadSlots(teacherFilter);
        } catch (err) {
          toast.error(err.message || 'Failed to cancel meeting');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <div className="parent-ptm-section">
      <div className="form-card" style={{ padding: 16, marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>Your meetings</h3>
        {bookings.length === 0 ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>No meetings booked yet.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Time</th><th>Teacher</th><th>Child</th><th>Location</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_id}>
                    <td>{b.date}</td>
                    <td>{b.start_time}-{b.end_time}</td>
                    <td>{b.teacher_name}</td>
                    <td>{b.student_name}</td>
                    <td>{b.location || '—'}</td>
                    <td>
                      <button className="btn-icon" title="Cancel" disabled={busyId === b.booking_id} onClick={() => handleCancelBooking(b.booking_id)}>
                        <X size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="form-card" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>Book a meeting</h3>
        <div className="form-group" style={{ maxWidth: 260, marginBottom: 12 }}>
          <label>Filter by teacher</label>
          <select value={teacherFilter} onChange={(e) => setTeacherFilter(e.target.value)}>
            <option value="">All teachers</option>
            {teachers.map((t) => (
              <option key={t.id} value={t.id}>{t.full_name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : slots.length === 0 ? (
          <p style={{ fontSize: 13, color: '#6b7280' }}>No open slots right now — check back later.</p>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr><th>Date</th><th>Time</th><th>Teacher</th><th>Location</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {slots.map((s) => (
                  <tr key={s.id}>
                    <td>{s.date}</td>
                    <td>{s.start_time}-{s.end_time}</td>
                    <td>{s.teacher_name}</td>
                    <td>{s.location || '—'}</td>
                    <td>
                      <button className="btn btn-sm btn-primary" onClick={() => startBooking(s.id)}>
                        <CalendarCheck size={14} /> Book
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {bookingSlotId && (
          <div className="modal-overlay" onClick={() => setBookingSlotId(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Confirm booking</h2>
                <button className="modal-close" onClick={() => setBookingSlotId(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-form">
                <div className="form-group">
                  <label>Child</label>
                  <select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
                    {(kids || []).map((c) => (
                      <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>What would you like to discuss? (optional)</label>
                  <textarea rows={3} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
                </div>
                <button className="btn btn-primary" onClick={confirmBooking} disabled={submitting}>
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default ParentPTMSection;
